import { useCallback } from 'react';
import { useApi, useCaller } from '@/hooks/api.hook';
import { getToken, setToken } from '@/utils/common';
import type {
  EntityApplication,
  EntityContext,
  EntitySummary,
  SwitchEntityResponse,
} from '@/types/entity-context';

const LAST_ACTIVE_ENTITY_KEY = 'last_active_entity_id';

export function tokenHasEntityContext(): boolean {
  const token = getToken();
  if (!token) return false;

  try {
    const encoded = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    if (!encoded) return false;
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
    return Boolean(
      payload.active_entity_id &&
        payload.membership_id &&
        payload.membership_version,
    );
  } catch {
    return false;
  }
}

export function getLastActiveEntityId(): string | null {
  return localStorage.getItem(LAST_ACTIVE_ENTITY_KEY);
}

export function chooseCurrentApplication(applications: EntityApplication[]): EntityApplication | null {
  const priority: Record<EntityApplication['status'], number> = {
    UnderReview: 4,
    Rejected: 3,
    Draft: 2,
    Approved: 1,
  };

  return [...applications].sort((left, right) => {
    const statusDifference = priority[right.status] - priority[left.status];
    if (statusDifference !== 0) return statusDifference;
    return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
  })[0] ?? null;
}

export const useEntityContextService = () => {
  const api = useApi('');
  const caller = useCaller();

  const getCurrentEntityContext = useCallback(async (): Promise<EntityContext | null> => {
    const response = await caller<ApiResult<EntityContext>>(
      () => api.get('/v1/identity/me/context'),
      { loadingKey: 'get-current-entity-context', returnErrorResponse: true },
    );
    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const listMyEntities = useCallback(async (): Promise<EntitySummary[]> => {
    const response = await caller<ApiResult<EntitySummary[]>>(
      () => api.get('/v1/entities'),
      { loadingKey: 'list-my-entities', returnErrorResponse: true },
    );
    return response?.isSuccess && Array.isArray(response.data) ? response.data : [];
  }, [api, caller]);

  const switchActiveEntity = useCallback(async (entityId: string): Promise<SwitchEntityResponse | null> => {
    const response = await caller<ApiResult<SwitchEntityResponse>>(
      () => api.post('/v1/identity/auth/switch-entity', { entityId }),
      { loadingKey: 'switch-active-entity', returnErrorResponse: true },
    );
    if (!response?.isSuccess || !response.data) return null;

    setToken(response.data.accessToken);
    localStorage.setItem(LAST_ACTIVE_ENTITY_KEY, response.data.context.entityId);
    return response.data;
  }, [api, caller]);

  const listMyEntityApplications = useCallback(async (): Promise<EntityApplication[]> => {
    const response = await caller<ApiResult<EntityApplication[]>>(
      () => api.get('/v1/entity-applications'),
      { loadingKey: 'list-entity-applications', returnErrorResponse: true },
    );
    return response?.isSuccess && Array.isArray(response.data) ? response.data : [];
  }, [api, caller]);

  const getEntityApplication = useCallback(async (applicationId: string): Promise<EntityApplication | null> => {
    const response = await caller<ApiResult<EntityApplication>>(
      () => api.get(`/v1/entity-applications/${encodeURIComponent(applicationId)}`),
      { loadingKey: 'get-entity-application', returnErrorResponse: true },
    );
    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const createEntityApplication = useCallback(async (
    draftName: string | null = null,
  ): Promise<EntityApplication | null> => {
    const response = await caller<ApiResult<EntityApplication>>(
      () => api.post('/v1/entity-applications', { draftName }),
      { loadingKey: 'create-entity-application', returnErrorResponse: true },
    );
    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  return {
    getCurrentEntityContext,
    listMyEntities,
    switchActiveEntity,
    listMyEntityApplications,
    getEntityApplication,
    createEntityApplication,
  };
};
